import { Injectable, inject } from '@angular/core';
import { NotificationService, NotificationServiceInjector } from '@next/core-lib/notification';
import { UserSelectors } from '@next/core-lib/user';
import { createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';

import {
  map,
  switchMap,
  withLatestFrom,
  catchError,
  debounceTime,
  groupBy,
  mergeMap,
  tap,
} from 'rxjs/operators';
import { LoadCropFieldsRequest } from 'src/app/registration/store/actions/crop-field.actions';
import { AppState } from 'src/app/shared/models';

import * as SoilAnalysisActions from './soil-analysis.actions';
import {
  SoilAnalysisLinkedEvents,
  SoilAnalysisLinkedFinalizedEvent,
} from '../models/soil-analysis.model';

@Injectable()
export class SoilAnalysisNotificationEffects {
  private readonly notification = inject<NotificationService>(NotificationServiceInjector);
  private readonly store = inject<Store<AppState>>(Store);

  soilAnalysisLinkedSuccess$ = createEffect(() =>
    this.notification
      .on<SoilAnalysisLinkedFinalizedEvent>(SoilAnalysisLinkedEvents.soilAnalysisLinkedFinalized)
      .pipe(
        map((event) => {
          if (typeof event === 'string') {
            const parsedEvent = JSON.parse(event);

            return {
              cropFieldId: parsedEvent.CropFieldId,
              customerId: parsedEvent.CustomerId,
              timestamp: new Date(),
            };
          }
          return event;
        }),
        groupBy((event) => event.customerId),
        mergeMap((customerGroup) => {
          return customerGroup.pipe(
            tap(() => {
              this.store.dispatch(SoilAnalysisActions.setSoilAnalysisLoading({ loading: true }));
            }),
            debounceTime(5000),
            withLatestFrom(this.store.select(UserSelectors.selectCustomer)),
            switchMap(([event, customer]) => {
              if (customer?.id !== event.customerId) {
                return of(SoilAnalysisActions.soilAnalysisLoadingComplete());
              }
              return of([
                SoilAnalysisActions.getSoilAnalyses(),
                new LoadCropFieldsRequest(),
                SoilAnalysisActions.soilAnalysisLoadingComplete(),
              ]).pipe(mergeMap((actions) => actions));
            }),
            catchError(() => {
              return EMPTY;
            }),
          );
        }),
      ),
  );
}
